import { formatISO9075 } from 'date-fns';
import { f7, useStore } from 'framework7-react';
import { Router } from 'framework7/types';
import { FC, useEffect, useState } from 'react';
import { getValidWarranties } from '../WarrantyManagement/WarrantyManagementHelper';
import { TroubleShooting } from './TroubleShooting/TroubleShooting';
import { paths } from '@/config/paths';
import { store } from '@/config/store';
import { callCenterDivision } from '@/consts/callCenter';
import { termType } from '@/consts/terms';
import {
  WARRANTY_SERVICE_DISABLED_MESSAGE,
  warrantyType,
} from '@/consts/warranty';
import { useMAffiliateSettings } from '@/hooks/api/info/useFetchMAffiliateSettings';
import { useGps } from '@/hooks/api/other/useGps';
import { useTerm } from '@/hooks/api/term/useTerm';
import { Car } from '@/types/api/customerApi';
import { isDelivered, openAlertWarrantyExpried } from '@/utils/car';
import { storeDispatch } from '@/utils/store';
import { getRandomNumbericString } from '@/utils/utils';
import { getPreferredCurrentWarranty } from '@/utils/warranty';

type TroubleShootingPageProps = {
  f7router: Router.Router;
};

export const TroubleShootingPage: FC<TroubleShootingPageProps> = ({
  f7router,
}) => {
  const { customerInfo, authInfo, currentWarranty, warranties, term } =
    store.state;
  const mAffiliateId = customerInfo.m_affiliate_id;
  const phoneNumber = customerInfo.phone_no1 ?? customerInfo.phone_no2 ?? '';
  const { data } = useMAffiliateSettings({ m_affiliate_id: mAffiliateId });
  const { data: roadService } = useTerm(
    termType.ROAD_SERVICE,
    '',
    !!term.roadService,
  );
  const callCenterTel = data?.data.callcenter_tel;
  const { sendGps } = useGps(true, callCenterDivision.ROAD_SERVICE);
  const [validWarranty] = getValidWarranties(warranties);
  const serviceWarranty = getPreferredCurrentWarranty(warranties);
  const isExistDefectPlan = validWarranty?.append_warranties?.some(
    (warranty) => warranty.warranty_type === warrantyType.DEFECT,
  );
  const [isOpenWarrantyRequired, setIsOpenWarrantyRequired] = useState(false);
  const selectedCar = useStore(store, 'carItemInfo') as Required<Car>;

  const selectServiceWarranty = async () => {
    if (!serviceWarranty) return;
    if (
      currentWarranty?.warranty_no === serviceWarranty.warranty_no &&
      currentWarranty?.branch_no === serviceWarranty.branch_no
    ) {
      return;
    }
    await storeDispatch(
      'setCurrentWarranty',
      'currentWarranty',
      serviceWarranty,
    );
  };

  const handleCallRoadService = () => {
    if (serviceWarranty?.warranty_no && !validWarranty) {
      return openAlertWarrantyExpried();
    }
    if (!serviceWarranty?.warranty_no) {
      return setIsOpenWarrantyRequired(true);
    }
    window.location.href = 'tel://' + data?.data.callcenter_tel;
  };

  // REVIEW: ロードサービスと電話番号同じでよいのか
  const handleCallSupportCenter = () => {
    window.location.href = 'tel://' + data?.data.callcenter_tel;
  };

  const handleClickProblem = async () => {
    // warranty_flg=0 の加盟法人は保証サービス停止状態のため修理依頼導線を停止
    if (!data?.data.warranty_flg) {
      f7.dialog.alert(WARRANTY_SERVICE_DISABLED_MESSAGE);
      return;
    }
    if (isDelivered(selectedCar.delivery_complete_flg)) {
      if (!selectedCar?.warranties?.[0]?.warranty_no || !isExistDefectPlan) {
        return setIsOpenWarrantyRequired(true);
      }
      if (!validWarranty) {
        return openAlertWarrantyExpried();
      }
      await selectServiceWarranty();
      return f7.view.main.router.navigate(paths.problem);
    }
    if (selectedCar?.warranties?.[0]?.warranty_no) {
      return openAlertWarrantyExpried();
    }
    setIsOpenWarrantyRequired(true);
  };

  const handleClickTrouble = async () => {
    // warranty_flg=0 の加盟法人は保証サービス停止状態のためロードサービス導線を停止
    if (!data?.data.warranty_flg) {
      f7.dialog.alert(WARRANTY_SERVICE_DISABLED_MESSAGE);
      return;
    }
    if (!serviceWarranty?.warranty_no) {
      return setIsOpenWarrantyRequired(true);
    }
    await selectServiceWarranty();
    f7.view.main.router.navigate(paths.trouble);
  };

  const handleClickWarrantyParts = async () => {
    await selectServiceWarranty();
    f7router.navigate(paths.warrantyParts);
  };

  const handleClickAccident = () => {
    f7.view.main.router.navigate(paths.accident);
  };

  const sendGpsInfo = (
    longitude: number,
    latitude: number,
    gps_time: string,
  ) => {
    const nowTime = formatISO9075(new Date(), {
      format: 'basic',
    }).replace(' ', '');
    gps_time = nowTime; // Androidから連携されるlocation.timeはdatetime型ではないため、フロント側の時間を利用する
    const params = {
      m_customer_id: authInfo.m_customer_id,
      warranty_no: serviceWarranty?.warranty_no ?? '',
      branch_no: serviceWarranty?.branch_no ?? undefined,
      service_queue: getRandomNumbericString(7),
      tel_no: phoneNumber.replace(/-/g, ''),
      service_tel_no: callCenterTel?.replace(/-/g, '') ?? '',
      longitude,
      latitude,
      gps_time,
    };
    sendGps(params);
  };

  window['onGetGps'] = (
    longitude: number,
    latitude: number,
    gps_time: string,
  ) => {
    sendGpsInfo(longitude, latitude, gps_time);
  };

  // ネイティブアプリの位置情報を許可していない状態でロードサービスを押した場合に呼ばれる
  window['onRequestRoadServiceWithoutGps'] = () => {
    window.location.href = 'tel://' + data?.data.callcenter_tel;
  };

  useEffect(() => {
    if (roadService) {
      const newTerm = { ...term, roadService: roadService.term };
      storeDispatch('setTerm', 'term', newTerm);
    }
  }, [roadService, term]);

  return (
    <TroubleShooting
      f7router={f7router}
      warranty={serviceWarranty}
      isOpenWarrantyRequired={isOpenWarrantyRequired}
      setIsOpenWarrantyRequired={setIsOpenWarrantyRequired}
      handleCallRoadService={handleCallRoadService}
      handleClickAccident={handleClickAccident}
      handleCallSupportCenter={handleCallSupportCenter}
      handleClickTrouble={handleClickTrouble}
      handleClickProblem={handleClickProblem}
      handleClickWarrantyParts={handleClickWarrantyParts}
    />
  );
};
